import { useState, useMemo } from 'react';
import { useDB } from '../../hooks/useDB';
import { formatDate, today } from '../../data/constants';

// ─── Constantes ───────────────────────────────────────────────────────────────
const STATUTS = [
  { key:'present',  label:'Présent',          icon:'✅', color:'var(--success)',  bg:'rgba(34,197,94,0.1)'  },
  { key:'absent',   label:'Absent',            icon:'❌', color:'var(--danger)',   bg:'rgba(220,38,38,0.1)'  },
  { key:'retard',   label:'En retard',         icon:'⏰', color:'var(--warning)',  bg:'rgba(180,83,9,0.1)'   },
  { key:'hs',       label:'H. supplémentaires',icon:'➕', color:'var(--bleu)',     bg:'rgba(0,48,135,0.1)'   },
];

const STATUT_VALID_HS = [
  { key:'en_attente',     label:'En attente',          icon:'⏳', color:'var(--warning)' },
  { key:'valide_manager', label:'Validé manager',       icon:'👔', color:'var(--cyan)'   },
  { key:'valide_rh',      label:'Validé RH',            icon:'✅', color:'var(--success)' },
  { key:'refuse',         label:'Refusé',               icon:'❌', color:'var(--danger)'  },
];

// ─── Hook partagé pour les présences ─────────────────────────────────────────
export function usePresences() {
  return useDB('cmob_presences', []);
}

// ─── Utilitaires ──────────────────────────────────────────────────────────────
function calcH(d, f) {
  if (!d || !f) return 0;
  const [dh,dm] = d.split(':').map(Number);
  const [fh,fm] = f.split(':').map(Number);
  return Math.max(0, ((fh*60+fm)-(dh*60+dm))/60);
}

function getStatut(s) {
  return STATUTS.find(x => x.key === s) || STATUTS[0];
}

// ─── Modale déclaration hors créneau ─────────────────────────────────────────
function ModaleHorsCreneau({ existant, onSave, onClose, nomSalarie, isManager }) {
  const [date,       setDate]       = useState(existant?.date       || today());
  const [typeHS,     setTypeHS]     = useState(existant?.typeHS     || 'HS');
  const [heureDebut, setHeureDebut] = useState(existant?.heureDebut || '');
  const [heureFin,   setHeureFin]   = useState(existant?.heureFin   || '');
  const [motif,      setMotif]      = useState(existant?.hsMotif    || '');
  const [note,       setNote]       = useState(existant?.note       || '');

  const hReel = calcH(heureDebut, heureFin);

  const save = () => {
    if (!date || !heureDebut || !heureFin || hReel <= 0) return;
    onSave({
      date,
      typeHS,
      heureDebut,
      heureFin,
      hReelles:   hReel.toFixed(2),
      hSupp:      hReel.toFixed(2),
      hsMotif:    motif,
      note,
      statut:     'hs',
      horsCreneauManual: true,
      hsStatut:   existant?.hsStatut || 'en_attente',
    });
    onClose();
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',
      alignItems:'center',justifyContent:'center',zIndex:1000,padding:16}}>
      <div style={{background:'var(--card)',borderRadius:16,width:'100%',maxWidth:460,
        boxShadow:'0 8px 40px rgba(0,0,0,0.2)',overflow:'hidden'}}>
        <div style={{background:'linear-gradient(135deg,#7c3aed,#003087)',
          padding:'18px 22px',color:'#fff'}}>
          <div style={{fontWeight:800,fontSize:'1rem'}}>
            ➕ Déclarer des heures hors créneau
          </div>
          {nomSalarie && <div style={{opacity:.8,fontSize:'0.8rem',marginTop:3}}>{nomSalarie}</div>}
        </div>
        <div style={{padding:'20px 22px',display:'flex',flexDirection:'column',gap:14}}>
          {/* Date */}
          <div className="form-group" style={{margin:0}}>
            <label>Date *</label>
            <input type="date" value={date} onChange={e=>setDate(e.target.value)}
              max={today()}/>
          </div>
          {/* Type HS/HC */}
          <div>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--muted)',
              textTransform:'uppercase',marginBottom:8}}>Type d'heures *</div>
            <div style={{display:'flex',gap:10}}>
              {[['HS','⬆️ Heures supplémentaires','#003087'],['HC','🔄 Heures complémentaires','#7c3aed']].map(([k,l,c])=>(
                <div key={k} onClick={()=>setTypeHS(k)}
                  style={{flex:1,padding:'10px 14px',borderRadius:10,cursor:'pointer',
                    textAlign:'center',userSelect:'none',transition:'all 0.15s',
                    border:`2px solid ${typeHS===k?c:'var(--border)'}`,
                    background:typeHS===k?`${c}12`:'var(--bg)',
                    fontWeight:700,fontSize:'0.85rem',
                    color:typeHS===k?c:'var(--muted)'}}>
                  {l}
                </div>
              ))}
            </div>
            <div style={{fontSize:'0.72rem',color:'var(--muted)',marginTop:6}}>
              {typeHS==='HS' ? 'Au-delà de la durée légale du travail (35h/semaine)' : 'Pour les temps partiels (entre durée contrat et 35h)'}
            </div>
          </div>
          {/* Heures */}
          <div>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--muted)',
              textTransform:'uppercase',marginBottom:8}}>Heures travaillées *</div>
            <div style={{display:'flex',gap:10,alignItems:'center'}}>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <label style={{fontSize:'0.78rem',color:'var(--muted)'}}>Début</label>
                <input type="time" value={heureDebut} onChange={e=>setHeureDebut(e.target.value)}
                  style={{padding:'6px 10px',borderRadius:8,border:'1.5px solid var(--border)',
                    fontFamily:'inherit',fontSize:'0.85rem'}}/>
              </div>
              <span style={{color:'var(--muted)'}}>→</span>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <label style={{fontSize:'0.78rem',color:'var(--muted)'}}>Fin</label>
                <input type="time" value={heureFin} onChange={e=>setHeureFin(e.target.value)}
                  style={{padding:'6px 10px',borderRadius:8,border:'1.5px solid var(--border)',
                    fontFamily:'inherit',fontSize:'0.85rem'}}/>
              </div>
              {hReel > 0 && (
                <span style={{fontWeight:800,fontSize:'0.95rem',color:'#7c3aed'}}>
                  = {hReel.toFixed(1)}h
                </span>
              )}
            </div>
          </div>
          {/* Motif */}
          <div className="form-group" style={{margin:0}}>
            <label>Motif *</label>
            <input value={motif} onChange={e=>setMotif(e.target.value)}
              placeholder="Ex: Remplacement, événement exceptionnel, match..."/>
          </div>
          {/* Note */}
          <div className="form-group" style={{margin:0}}>
            <label>Note (optionnel)</label>
            <textarea rows={2} value={note} onChange={e=>setNote(e.target.value)}
              placeholder="Informations complémentaires..."/>
          </div>
          <div style={{padding:'10px 12px',borderRadius:8,background:'rgba(124,58,237,0.06)',
            border:'1px solid rgba(124,58,237,0.2)',fontSize:'0.78rem',color:'#7c3aed',fontWeight:600}}>
            ℹ️ Ces heures seront soumises à validation Manager puis RH.
          </div>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button className="btn btn-primary"
              disabled={!date||!heureDebut||!heureFin||!motif.trim()||hReel<=0}
              onClick={save}>
              📤 Soumettre
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Modale de déclaration (salarié ou manager) ───────────────────────────────
function ModaleDeclaration({ creneau, existant, onSave, onClose, isManager, nomSalarie }) {
  const [statut,     setStatut]     = useState(existant?.statut     || 'present');
  const [heureDebut, setHeureDebut] = useState(existant?.heureDebut || creneau?.debut || '');
  const [heureFin,   setHeureFin]   = useState(existant?.heureFin   || creneau?.fin   || '');
  const [hsMotif,    setHsMotif]    = useState(existant?.hsMotif     || '');
  const [note,       setNote]       = useState(existant?.note        || '');

  const hPrevu  = calcH(creneau?.debut, creneau?.fin);
  const hReel   = calcH(heureDebut, heureFin);
  const hSupp   = Math.max(0, hReel - hPrevu).toFixed(2);
  const isHS    = statut === 'hs' || parseFloat(hSupp) > 0;

  const save = () => {
    onSave({
      statut,
      heureDebut,
      heureFin,
      hReelles: hReel.toFixed(2),
      hSupp: isHS ? hSupp : '0',
      hsMotif: isHS ? hsMotif : '',
      note,
      hsStatut: isHS ? (existant?.hsStatut || 'en_attente') : null,
    });
    onClose();
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',
      alignItems:'center',justifyContent:'center',zIndex:1000,padding:16}}>
      <div style={{background:'var(--card)',borderRadius:16,width:'100%',maxWidth:480,
        boxShadow:'0 8px 40px rgba(0,0,0,0.2)',overflow:'hidden'}}>

        {/* Header */}
        <div style={{background:'linear-gradient(135deg,var(--bleu),#005299)',
          padding:'18px 22px',color:'#fff'}}>
          <div style={{fontWeight:800,fontSize:'1rem'}}>
            {isManager ? `👔 Pointage — ${nomSalarie}` : '⏱️ Déclarer mes heures'}
          </div>
          <div style={{opacity:.8,fontSize:'0.8rem',marginTop:3}}>
            {formatDate(creneau?.date)} · Prévu {creneau?.debut} – {creneau?.fin}
            {hPrevu > 0 && ` (${hPrevu.toFixed(1)}h)`}
          </div>
        </div>

        <div style={{padding:'20px 22px',display:'flex',flexDirection:'column',gap:14}}>

          {/* Statut */}
          <div>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--muted)',
              textTransform:'uppercase',marginBottom:8}}>Statut</div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {STATUTS.map(s => (
                <div key={s.key} onClick={()=>setStatut(s.key)}
                  style={{padding:'8px 14px',borderRadius:10,cursor:'pointer',userSelect:'none',
                    border:`2px solid ${statut===s.key?s.color:'var(--border)'}`,
                    background:statut===s.key?s.bg:'var(--bg)',transition:'all 0.15s',
                    fontSize:'0.82rem',fontWeight:700,color:statut===s.key?s.color:'var(--muted)'}}>
                  {s.icon} {s.label}
                </div>
              ))}
            </div>
          </div>

          {/* Heures réelles */}
          <div>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--muted)',
              textTransform:'uppercase',marginBottom:8}}>Heures réelles</div>
            <div style={{display:'flex',gap:10,alignItems:'center'}}>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <label style={{fontSize:'0.78rem',color:'var(--muted)'}}>Début</label>
                <input type="time" value={heureDebut} onChange={e=>setHeureDebut(e.target.value)}
                  style={{padding:'6px 10px',borderRadius:8,border:'1.5px solid var(--border)',
                    fontFamily:'inherit',fontSize:'0.85rem'}}/>
              </div>
              <span style={{color:'var(--muted)'}}>→</span>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <label style={{fontSize:'0.78rem',color:'var(--muted)'}}>Fin</label>
                <input type="time" value={heureFin} onChange={e=>setHeureFin(e.target.value)}
                  style={{padding:'6px 10px',borderRadius:8,border:'1.5px solid var(--border)',
                    fontFamily:'inherit',fontSize:'0.85rem'}}/>
              </div>
              {hReel > 0 && (
                <span style={{fontWeight:700,fontSize:'0.9rem',
                  color: parseFloat(hSupp)>0 ? 'var(--bleu)' : 'var(--success)'}}>
                  = {hReel.toFixed(1)}h
                </span>
              )}
            </div>
          </div>

          {/* Heures supp */}
          {parseFloat(hSupp) > 0 && (
            <div style={{padding:'12px 14px',borderRadius:10,
              background:'rgba(0,48,135,0.06)',border:'1.5px solid rgba(0,48,135,0.2)'}}>
              <div style={{fontWeight:700,fontSize:'0.85rem',color:'var(--bleu)',marginBottom:8}}>
                ➕ {hSupp}h supplémentaires détectées — soumises à validation
              </div>
              <input value={hsMotif} onChange={e=>setHsMotif(e.target.value)}
                placeholder="Motif des heures supplémentaires..."
                style={{width:'100%',padding:'7px 10px',borderRadius:8,
                  border:'1.5px solid rgba(0,48,135,0.2)',fontFamily:'inherit',fontSize:'0.82rem'}}/>
            </div>
          )}

          {/* Note */}
          <div>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--muted)',
              textTransform:'uppercase',marginBottom:6}}>Note (optionnel)</div>
            <textarea value={note} onChange={e=>setNote(e.target.value)} rows={2}
              placeholder="Observation, commentaire..."
              style={{width:'100%',padding:'8px 10px',borderRadius:8,resize:'vertical',
                border:'1.5px solid var(--border)',fontFamily:'inherit',fontSize:'0.82rem'}}/>
          </div>

          {/* Actions */}
          <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:4}}>
            <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button className="btn btn-primary" onClick={save}>
              💾 Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── VUE SALARIÉ : déclarer ses heures sur Mon Planning ──────────────────────
export function PointageSalarie({ user, planningH, presences, setPresences }) {
  const [modale,    setModale]    = useState(null); // { creneau, existant }
  const [filtreTab,    setFiltreTab]    = useState('a_declarer'); // a_declarer | tous
  const [showHorsCreneau, setShowHorsCreneau] = useState(false);

  const myStaffId = user?.staffId;
  const safePH    = planningH || [];

  // Créneaux passés du salarié (les 30 derniers jours)
  const il30j = new Date();
  il30j.setDate(il30j.getDate() - 30);
  const limit30 = il30j.toISOString().slice(0,10);

  const mesCreneaux = safePH
    .filter(p => !p._saisonPlan && String(p.staffId) === String(myStaffId) && p.date <= today() && p.date >= limit30)
    .sort((a,b) => b.date.localeCompare(a.date));

  const getPresence = (creneauId) =>
    (presences||[]).find(p => p.creneauId === creneauId && String(p.staffId) === String(myStaffId));

  const aDeClarer = mesCreneaux.filter(c => !getPresence(c.id));
  // const declares = mesCreneaux.filter(c => getPresence(c.id)); // réservé usage futur

  const liste = filtreTab === 'a_declarer' ? aDeClarer : mesCreneaux;

  const sauver = (creneau, data) => {
    const existing = getPresence(creneau.id);
    const entry = {
      id:         existing?.id || `pres_${Date.now()}_${Math.random()}`,
      creneauId:  creneau.id,
      staffId:    myStaffId,
      date:       creneau.date,
      nomCreneau: creneau.nom || creneau.type || 'Créneau',
      declarePar: 'salarie',
      dateDecla:  new Date().toISOString(),
      ...data,
    };
    setPresences(prev => {
      const filtered = (prev||[]).filter(p => p.id !== entry.id);
      return [...filtered, entry];
    });
  };

  return (
    <div>
      <div style={{marginBottom:14,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
        <div style={{fontWeight:700,fontSize:'0.95rem'}}>⏱️ Mes pointages</div>
        <div style={{flex:1}}/>
        <button className="btn btn-secondary btn-sm" onClick={()=>setShowHorsCreneau(true)}
          style={{color:'#7c3aed',borderColor:'#7c3aed'}}>
          ➕ Hors créneau
        </button>
        <div style={{display:'flex',gap:4}}>
          {[['a_declarer',`À déclarer (${aDeClarer.length})`],['tous',`Tous (${mesCreneaux.length})`]].map(([k,l])=>(
            <button key={k} onClick={()=>setFiltreTab(k)}
              className={`btn btn-sm ${filtreTab===k?'btn-primary':'btn-secondary'}`}>{l}</button>
          ))}
        </div>
      </div>

      {liste.length === 0 ? (
        <div style={{textAlign:'center',padding:'32px 0',color:'var(--muted)'}}>
          <div style={{fontSize:'2rem',marginBottom:8}}>✅</div>
          <div style={{fontWeight:600}}>
            {filtreTab==='a_declarer' ? 'Tous vos créneaux sont déclarés !' : 'Aucun créneau sur 30 jours'}
          </div>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {liste.map(c => {
            const pres = getPresence(c.id);
            const st   = pres ? getStatut(pres.statut) : null;
            const hsInfo = pres?.hsStatut ? STATUT_VALID_HS.find(x=>x.key===pres.hsStatut) : null;
            return (
              <div key={c.id} style={{background:'var(--card)',borderRadius:10,
                border:`1.5px solid ${pres?'rgba(0,48,135,0.2)':'var(--border)'}`,
                padding:'12px 16px',display:'flex',alignItems:'center',gap:12}}>
                <div style={{width:44,height:44,borderRadius:10,flexShrink:0,
                  background:pres?st.bg:'var(--bg3)',display:'flex',alignItems:'center',
                  justifyContent:'center',fontSize:'1.3rem'}}>
                  {pres ? st.icon : '⭕'}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:'0.88rem'}}>{c.nom||c.type||'Créneau'}</div>
                  <div style={{fontSize:'0.75rem',color:'var(--muted)',marginTop:2}}>
                    {formatDate(c.date)} · Prévu {c.debut} – {c.fin}
                    {pres && ` · Réel ${pres.heureDebut} – ${pres.heureFin} (${pres.hReelles}h)`}
                  </div>
                  {pres && parseFloat(pres.hSupp) > 0 && (
                    <div style={{display:'flex',alignItems:'center',gap:6,marginTop:4}}>
                      <span style={{fontSize:'0.72rem',padding:'2px 8px',borderRadius:8,fontWeight:700,
                        background:hsInfo?.color?`${hsInfo.color}15`:'var(--bg3)',
                        color:hsInfo?.color||'var(--muted)'}}>
                        {hsInfo?.icon} {pres.hSupp}h supp — {hsInfo?.label||'En attente'}
                      </span>
                    </div>
                  )}
                </div>
                <button className="btn btn-secondary btn-sm"
                  onClick={()=>setModale({creneau:c, existant:pres})}>
                  {pres ? '✏️ Modifier' : '📝 Déclarer'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {modale && (
        <ModaleDeclaration
          creneau={modale.creneau}
          existant={modale.existant}
          isManager={false}
          onSave={(data) => sauver(modale.creneau, data)}
          onClose={()=>setModale(null)}
        />
      )}

      {showHorsCreneau && (
        <ModaleHorsCreneau
          onSave={(data) => {
            const entry = {
              id:              `hc_${Date.now()}_${Math.random()}`,
              creneauId:       null,
              staffId:         myStaffId,
              nomCreneau:      `${data.typeHS} hors créneau`,
              declarePar:      'salarie',
              dateDecla:       new Date().toISOString(),
              ...data,
            };
            setPresences(prev => [...(prev||[]), entry]);
          }}
          onClose={()=>setShowHorsCreneau(false)}
          isManager={false}
        />
      )}
    </div>
  );
}

// ─── VUE MANAGER : pointage de l'équipe ──────────────────────────────────────
export function PointageManager({ user, users, planningH, presences, setPresences, staff, isRH }) {
  const [modale,    setModale]    = useState(null);
  const [dateFil,   setDateFil]   = useState(today());
  const [onglet,    setOnglet]    = useState('pointage');
  const [showHorsCreneau, setShowHorsCreneau] = useState(null); // staffId cible // pointage | hs_validation

  const myUserId  = user?.id;

  // Salariés de l'équipe
  const myStaffIds = useMemo(() => {
    if (isRH) return (staff||[]).map(s=>s.id);
    return (users||[])
      .filter(u => (u.managerIds||[]).includes(myUserId))
      .map(u => u.staffId)
      .filter(Boolean);
  }, [users, myUserId, staff, isRH]);

  const safePH = planningH || [];

  // Créneaux du jour sélectionné pour l'équipe
  const creneauxJour = safePH
    .filter(p => !p._saisonPlan && p.date === dateFil && myStaffIds.includes(p.staffId))
    .sort((a,b) => (a.debut||'').localeCompare(b.debut||''));

  const getPresence = (creneauId, staffId) =>
    (presences||[]).find(p => p.creneauId === creneauId && String(p.staffId) === String(staffId));

  const getSalarie = (staffId) =>
    (staff||[]).find(s => s.id === staffId);

  const sauver = (creneau, staffId, data) => {
    const existing = getPresence(creneau.id, staffId);
    const sal = getSalarie(staffId);
    const entry = {
      id:         existing?.id || `pres_${Date.now()}_${Math.random()}`,
      creneauId:  creneau.id,
      staffId,
      date:       creneau.date,
      nomCreneau: creneau.nom || creneau.type || 'Créneau',
      declarePar: 'manager',
      managerId:  myUserId,
      dateDecla:  new Date().toISOString(),
      nomSalarie: sal ? `${sal.prenom} ${sal.nom}` : '',
      ...data,
    };
    setPresences(prev => {
      const filtered = (prev||[]).filter(p => p.id !== entry.id);
      return [...filtered, entry];
    });
  };

  // HS en attente de validation
  const hsEnAttente = (presences||[]).filter(p =>
    myStaffIds.includes(p.staffId) && parseFloat(p.hSupp||0) > 0 &&
    (isRH
      ? p.hsStatut === 'valide_manager' || p.hsStatut === 'en_attente'
      : p.hsStatut === 'en_attente')
  );

  const validerHS = (presId, decision) => {
    setPresences(prev => (prev||[]).map(p => p.id !== presId ? p : {
      ...p,
      hsStatut: decision === 'valider'
        ? (isRH ? 'valide_rh' : 'valide_manager')
        : 'refuse',
      hsValidePar: myUserId,
      hsValideDate: new Date().toISOString(),
    }));
  };

  // Bypass RH direct
  const bypassRH = (presId) => {
    setPresences(prev => (prev||[]).map(p => p.id !== presId ? p : {
      ...p, hsStatut:'valide_rh',
      hsValidePar: myUserId, hsValideDate: new Date().toISOString(),
    }));
  };

  return (
    <div>
      <div className="tabs" style={{marginBottom:16}}>
        <div className={`tab ${onglet==='pointage'?'active':''}`} onClick={()=>setOnglet('pointage')}>
          📋 Pointage équipe
        </div>
        <div className={`tab ${onglet==='hs_validation'?'active':''}`} onClick={()=>setOnglet('hs_validation')}>
          ➕ H. supp. à valider
          {hsEnAttente.length > 0 && (
            <span style={{marginLeft:6,background:'var(--danger)',color:'#fff',
              borderRadius:10,padding:'1px 6px',fontSize:'0.68rem',fontWeight:700}}>
              {hsEnAttente.length}
            </span>
          )}
        </div>
      </div>

      {onglet === 'pointage' && (
        <div>
          {/* Sélecteur de date */}
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,flexWrap:'wrap'}}>
            <input type="date" value={dateFil} onChange={e=>setDateFil(e.target.value)}
              style={{padding:'7px 12px',borderRadius:8,border:'1.5px solid var(--border)',
                fontFamily:'inherit',fontSize:'0.85rem'}}/>
            <button className="btn btn-secondary btn-sm" onClick={()=>setDateFil(today())}>
              Aujourd'hui
            </button>
            <button className="btn btn-secondary btn-sm" onClick={()=>setShowHorsCreneau('pick')}
              style={{color:'#7c3aed',borderColor:'#7c3aed'}}>
              ➕ Déclarer hors créneau
            </button>
            <span style={{fontSize:'0.78rem',color:'var(--muted)'}}>
              {creneauxJour.length} créneau(x) ce jour
            </span>
          </div>

          {creneauxJour.length === 0 ? (
            <div style={{textAlign:'center',padding:'32px',color:'var(--muted)'}}>
              <div style={{fontSize:'2rem',marginBottom:8}}>📅</div>
              <div>Aucun créneau prévu pour cette date</div>
            </div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {creneauxJour.map(c => {
                const sal  = getSalarie(c.staffId);
                const pres = getPresence(c.id, c.staffId);
                const st   = pres ? getStatut(pres.statut) : null;
                const nomSal = sal ? `${sal.prenom} ${sal.nom}` : `Salarié #${c.staffId}`;
                return (
                  <div key={c.id} style={{background:'var(--card)',borderRadius:10,
                    border:`1.5px solid ${pres?st.color+'44':'var(--border)'}`,
                    padding:'12px 16px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                    <div style={{width:38,height:38,borderRadius:'50%',flexShrink:0,
                      background:'var(--bleu)',color:'#fff',display:'flex',
                      alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:'0.85rem'}}>
                      {sal?.prenom?.[0]}{sal?.nom?.[0]}
                    </div>
                    <div style={{flex:1,minWidth:120}}>
                      <div style={{fontWeight:700,fontSize:'0.88rem'}}>{nomSal}</div>
                      <div style={{fontSize:'0.74rem',color:'var(--muted)'}}>
                        {c.nom||c.type} · {c.debut} – {c.fin}
                        {pres && ` · Réel: ${pres.heureDebut}–${pres.heureFin} (${pres.hReelles}h)`}
                      </div>
                    </div>
                    {/* Boutons statut rapide */}
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      {STATUTS.map(s => (
                        <button key={s.key} title={s.label}
                          onClick={()=>{
                            if (s.key !== 'hs') {
                              sauver(c, c.staffId, {
                                statut:s.key, heureDebut:c.debut, heureFin:c.fin,
                                hReelles: calcH(c.debut,c.fin).toFixed(2), hSupp:'0'
                              });
                            } else {
                              setModale({creneau:c, staffId:c.staffId, existant:pres, nomSalarie:nomSal});
                            }
                          }}
                          style={{padding:'5px 10px',borderRadius:8,cursor:'pointer',fontFamily:'inherit',
                            fontSize:'0.78rem',fontWeight:700,border:'1.5px solid',transition:'all 0.15s',
                            borderColor: pres?.statut===s.key ? s.color : 'var(--border)',
                            background:  pres?.statut===s.key ? s.bg    : 'var(--bg)',
                            color:       pres?.statut===s.key ? s.color : 'var(--muted)'}}>
                          {s.icon}
                        </button>
                      ))}
                      <button className="btn btn-secondary btn-sm"
                        onClick={()=>setModale({creneau:c, staffId:c.staffId, existant:pres, nomSalarie:nomSal})}>
                        ✏️
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {onglet === 'hs_validation' && (
        <div>
          {hsEnAttente.length === 0 ? (
            <div style={{textAlign:'center',padding:'32px',color:'var(--muted)'}}>
              <div style={{fontSize:'2rem',marginBottom:8}}>✅</div>
              <div>Aucune heure supplémentaire en attente</div>
            </div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {hsEnAttente.map(p => {
                const sal    = getSalarie(p.staffId);
                const hsInfo = STATUT_VALID_HS.find(x=>x.key===p.hsStatut);
                return (
                  <div key={p.id} style={{background:'var(--card)',borderRadius:12,
                    border:'1.5px solid rgba(0,48,135,0.2)',padding:'16px 18px'}}>
                    <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:10,flexWrap:'wrap'}}>
                      <div style={{width:36,height:36,borderRadius:'50%',background:'var(--bleu)',
                        color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',
                        fontWeight:800,fontSize:'0.82rem',flexShrink:0}}>
                        {sal?.prenom?.[0]}{sal?.nom?.[0]}
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700}}>{sal?`${sal.prenom} ${sal.nom}`:`Salarié #${p.staffId}`}</div>
                        <div style={{fontSize:'0.75rem',color:'var(--muted)'}}>
                          {formatDate(p.date)} · {p.nomCreneau}
                        </div>
                      </div>
                      <span style={{padding:'3px 10px',borderRadius:10,fontSize:'0.72rem',fontWeight:700,
                        background:`${hsInfo?.color||'var(--muted)'}15`,color:hsInfo?.color||'var(--muted)'}}>
                        {hsInfo?.icon} {hsInfo?.label}
                      </span>
                    </div>
                    <div style={{background:'rgba(0,48,135,0.05)',borderRadius:8,padding:'10px 14px',marginBottom:12}}>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:8,fontSize:'0.78rem'}}>
                        <div>
                          <div style={{color:'var(--muted)',fontWeight:700,marginBottom:2}}>Heures prévues</div>
                          <div style={{fontWeight:700}}>{p.heureDebut} – {p.heureFin}</div>
                        </div>
                        <div>
                          <div style={{color:'var(--muted)',fontWeight:700,marginBottom:2}}>Heures réelles</div>
                          <div style={{fontWeight:700}}>{p.hReelles}h</div>
                        </div>
                        <div>
                          <div style={{color:'var(--muted)',fontWeight:700,marginBottom:2}}>H. supplémentaires</div>
                          <div style={{fontWeight:700,color:'var(--bleu)'}}>+{p.hSupp}h</div>
                        </div>
                      </div>
                      {p.hsMotif && (
                        <div style={{marginTop:8,fontSize:'0.78rem',color:'var(--muted)',fontStyle:'italic'}}>
                          Motif : {p.hsMotif}
                        </div>
                      )}
                    </div>
                    <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                      <button className="btn btn-primary btn-sm"
                        onClick={()=>validerHS(p.id,'valider')}>
                        ✅ {isRH ? 'Valider (RH)' : 'Valider'}
                      </button>
                      <button className="btn btn-danger btn-sm"
                        onClick={()=>validerHS(p.id,'refuser')}>
                        ❌ Refuser
                      </button>
                      {!isRH && (
                        <button className="btn btn-secondary btn-sm"
                          onClick={()=>bypassRH(p.id)}
                          title="Valider directement sans passer par le RH">
                          ⚡ Bypass RH
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {modale && (
        <ModaleDeclaration
          creneau={modale.creneau}
          existant={modale.existant}
          isManager={true}
          nomSalarie={modale.nomSalarie}
          onSave={(data) => sauver(modale.creneau, modale.staffId, data)}
          onClose={()=>setModale(null)}
        />
      )}

      {showHorsCreneau && (
        <div>
          {showHorsCreneau === 'pick' ? (
            /* Sélectionner le salarié */
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',
              alignItems:'center',justifyContent:'center',zIndex:1000,padding:16}}>
              <div style={{background:'var(--card)',borderRadius:16,width:'100%',maxWidth:400,
                padding:'24px',boxShadow:'0 8px 40px rgba(0,0,0,0.2)'}}>
                <div style={{fontWeight:800,fontSize:'1rem',marginBottom:16}}>
                  ➕ Pour quel salarié ?
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:8,maxHeight:300,overflowY:'auto'}}>
                  {(staff||[]).map(s=>(
                    <div key={s.id} onClick={()=>setShowHorsCreneau(s.id)}
                      style={{padding:'10px 14px',borderRadius:10,cursor:'pointer',
                        border:'1.5px solid var(--border)',background:'var(--bg)',
                        display:'flex',alignItems:'center',gap:10,transition:'all 0.15s'}}
                      onMouseEnter={e=>e.currentTarget.style.borderColor='#7c3aed'}
                      onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}>
                      <div style={{width:32,height:32,borderRadius:'50%',background:'var(--bleu)',
                        color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',
                        fontWeight:800,fontSize:'0.8rem',flexShrink:0}}>
                        {s.prenom?.[0]}{s.nom?.[0]}
                      </div>
                      <div style={{fontWeight:600}}>{s.prenom} {s.nom}</div>
                    </div>
                  ))}
                </div>
                <div style={{marginTop:14,textAlign:'right'}}>
                  <button className="btn btn-secondary" onClick={()=>setShowHorsCreneau(null)}>Annuler</button>
                </div>
              </div>
            </div>
          ) : (
            <ModaleHorsCreneau
              nomSalarie={(()=>{const s=getSalarie(showHorsCreneau);return s?`${s.prenom} ${s.nom}`:''})()}
              isManager={true}
              onSave={(data) => {
                const sal = getSalarie(showHorsCreneau);
                const entry = {
                  id:          `hc_${Date.now()}_${Math.random()}`,
                  creneauId:   null,
                  staffId:     showHorsCreneau,
                  nomCreneau:  `${data.typeHS} hors créneau`,
                  nomSalarie:  sal ? `${sal.prenom} ${sal.nom}` : '',
                  declarePar:  'manager',
                  managerId:   myUserId,
                  dateDecla:   new Date().toISOString(),
                  ...data,
                };
                setPresences(prev => [...(prev||[]), entry]);
              }}
              onClose={()=>setShowHorsCreneau(null)}
            />
          )}
        </div>
      )}
    </div>
  );
}
